import type { Rng } from './ports';
import type { Card } from './types';

export const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', 'T', 'J', 'Q', 'K', 'A'] as const;
export const SUITS = ['s', 'h', 'd', 'c'] as const;

export type Rank = (typeof RANKS)[number];
export type Suit = (typeof SUITS)[number];

/** Create an ordered 52-card deck using pokersolver notation (rank + suit). */
export function createDeck(): Card[] {
  const deck: Card[] = [];
  for (const rank of RANKS) {
    for (const suit of SUITS) {
      deck.push(`${rank}${suit}`);
    }
  }
  return deck;
}

/** Return a shuffled copy of the deck using the provided RNG. Input is not mutated. */
export function shuffleDeck(deck: ReadonlyArray<Card>, rng: Rng): Card[] {
  return rng.shuffle(deck);
}

export type DealResult = {
  dealt: Card[];
  rest: Card[];
};

/** Deal `n` cards from the top of the deck. Returns dealt cards and remaining deck. */
export function deal(deck: ReadonlyArray<Card>, n: number): DealResult {
  if (!Number.isInteger(n) || n < 0) {
    throw new RangeError(`deal: n must be a non-negative integer, got ${n}`);
  }
  if (n > deck.length) {
    throw new RangeError(`deal: cannot deal ${n} cards from deck of size ${deck.length}`);
  }
  return {
    dealt: deck.slice(0, n),
    rest: deck.slice(n),
  };
}

/** Burn the top card and return the remaining deck. Standard NLHE pre-street convention. */
export function burn(deck: ReadonlyArray<Card>): { burned: Card; rest: Card[] } {
  if (deck.length === 0) {
    throw new RangeError('burn: deck is empty');
  }
  return {
    burned: deck[0] as Card,
    rest: deck.slice(1),
  };
}
